import { DatePicker, IDatePickerProps, ITextFieldProps, Stack } from "@fluentui/react";
import React, { useEffect, useState } from "react";
import { Control, Controller, ControllerRenderProps, FieldErrors } from "react-hook-form";

import { useDateRender } from "../../hooks";
import { dateTimeService } from "../../utils/date/DateTimeService";
import { FormValidationRules } from "../../utils/types/FormValidationRules";
import { getValidate } from "../../utils/validation/getValidate";
import { FailureSeverity, ValidationFailure } from "../../utils/validation/ValidationResult";
import { FormErrorMessage } from "./FormErrorMessage";
import { FormFailureMessages } from "./FormFailureMessages";
import { FormClearButton } from "./FromClearButton";

export interface FormDatePickerProp extends IDatePickerProps {
    label: string;
    name: string;
    control: Control;
    errors: FieldErrors;
    rules?: FormDatePickerRules;
}

export const FormDatePicker: React.FunctionComponent<FormDatePickerProp> = (props: FormDatePickerProp) => {
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
    const [warnings, setWarnings] = useState<ValidationFailure[]>([]);
    const { renderDate } = useDateRender();

    useEffect(() => {
        const formDateSelected = props.control.getValues(props.name);
        const utcDate = formDateSelected ? dateTimeService().getDateWithoutTimezone(formDateSelected) : undefined;

        setSelectedDate(utcDate);
    }, []);

    const clearWarnings = () => {
        setWarnings([]);
    };

    const render = (prop: ControllerRenderProps<Record<string, unknown>>) => {
        const setValue = (date: Date | null | undefined) => {
            clearWarnings();

            const utcDate = date ? dateTimeService().getDateWithoutTimezone(date) : undefined;

            setSelectedDate(utcDate);
            prop.onChange(utcDate || null);
        };

        const onClear = () => {
            clearWarnings();

            setSelectedDate(undefined);
            prop.onChange(null);
        };

        const formatDate = (date?: Date): string => {
            if (props.formatDate) {
                return props.formatDate(date);
            }

            return date ? renderDate(date) : "";
        };

        const value = selectedDate || props.control.getValues(props.name);

        return (
            <div style={{ position: "relative" }}>
                <DatePicker
                    onSelectDate={setValue}
                    data-testtype={"date"}
                    data-testid={props.name}
                    textField={
                        {
                            styles: {
                                fieldGroup: { border: props.errors[props.name] && "2px solid red", marginBottom: -5 }
                            },
                            errorMessage: undefined,
                            "data-testid": `${props.name}-input`
                        } as ITextFieldProps
                    }
                    value={value ? dateTimeService().getDateWithTimezone(value) : undefined}
                    isRequired={!!props.rules?.required}
                    label={props.label}
                    formatDate={formatDate}
                />
                {!!value && <FormClearButton disabled={!value} onClick={onClear} testId={`${props.name}-clear`}/>}
            </div>
        );
    };

    return (
        <Stack>
            <Controller
                name={props.name}
                control={props.control}
                rules={{
                    ...props.rules,
                    validate: getValidate<Date, FormDatePickerRules>(
                        props.rules,
                        props.control.formStateRef.current.submitCount,
                        setWarnings
                    )
                }}
                render={render}
            />
            <FormErrorMessage name={props.name} errors={props.errors} testId={`${props.name}-error`} />
            <FormFailureMessages
                failures={warnings}
                severity={FailureSeverity.Warning}
                testId={`${props.name}-warning`}
            />
        </Stack>
    );
};

export type FormDatePickerRules = FormValidationRules<Date>;
